import { ItemStatusSliceType } from "../../../services/redux/slices/itemStatusSlice";
import { OrderTypesEnum } from "../../enums";
import envConfig from "../../env.config";
import { AddonGroupType, ItemType, SubItemType } from "../../types";
import { getDisabledItemsPerOrderType } from "../orderType/orderType";
import * as itemDetailsHelp from "./itemDetails";
import {
  getItemOfferOutOfStockAndCustomDeliveryTime,
  handleItemOfferPrice,
  handleSubItemOfferPrice,
} from "./itemDetailsHelper";
const lodashClonedeep = require("lodash.clonedeep");

export const reCheckItemDetails = (
  itemStatus: ItemStatusSliceType,
  itemObj: ItemType,
  checkIfDeletedItemOrSubItem?: boolean,
  hasRoundingMethodAndLevel: boolean = false
) => {
  const data = {
    item: { ...itemObj },
    isOutOfStock: false,
    customDeliveryTime: 0,
  };

  const { offerItem, isOutOfStock, customDeliveryTime } =
    getItemOfferOutOfStockAndCustomDeliveryTime(itemStatus, itemObj.id);
  data.isOutOfStock = isOutOfStock;

  handleItemOfferPrice(data.item, offerItem, hasRoundingMethodAndLevel);

  if (
    checkIfDeletedItemOrSubItem &&
    (data.item.deletedAt ||
      data.item.subItems[0].deletedAt ||
      !data.item.itemCategories ||
      data.item.itemCategories.length === 0 ||
      // Pulled from the menu: still shown (in previous orders), but not
      // re-orderable. Without this an item stayed one tap away from a new cart
      // forever once it was in someone's history, priced at whatever the
      // catalog held — 0 for combo components, whose price lives in the parent.
      data.item.isMenuItem === false)
  ) {
    data.isOutOfStock = true;
  }
  data.customDeliveryTime = customDeliveryTime;

  return data;
};

export const checkSubItemDetails = (
  subItem: SubItemType,
  hasRoundingMethodAndLevel: boolean,
  itemStatus: ItemStatusSliceType
) => {
  const data = {
    subItem: lodashClonedeep(subItem),
    isOutOfStock: false,
  };

  if (!subItem.item) {
    data.isOutOfStock = true;

    return data;
  }

  const { offerItem, isOutOfStock } =
    getItemOfferOutOfStockAndCustomDeliveryTime(itemStatus, subItem.item!.id);
  data.isOutOfStock = isOutOfStock;

  handleSubItemOfferPrice(data.subItem, offerItem, hasRoundingMethodAndLevel);

  return data;
};

export const getAllUnavailableItemIds = (
  itemStatusRedux: ItemStatusSliceType,
  orderType?: OrderTypesEnum
) => {
  const outOfStockItemIds = itemStatusRedux.outOfStockItemsIds || [];

  const disabledItemsIds = itemDetailsHelp.getAllDisabledItems(
    itemStatusRedux,
    orderType
  );

  return {
    disabledItemsIds,
    outOfStockItemIds,
    allUnavailableItemIds: disabledItemsIds.concat(outOfStockItemIds),
  };
};

export const isItemHaveRequiredAddons = (AddonGroups: any) => {
  let isItemHaveRequiredAddons: boolean = false;

  for (let i = 0; i < AddonGroups?.length; i++) {
    const AddonGroup = AddonGroups[i];
    if (AddonGroup.minChoices > 0) {
      isItemHaveRequiredAddons = true;
      break;
    }
  }

  return isItemHaveRequiredAddons;
};

export const isItemHaveAddons = (addonGroups: AddonGroupType[]) => {
  return !!(addonGroups && addonGroups.length > 0);
};

export const getAllDisabledItems = (
  itemStatusRedux: ItemStatusSliceType,
  orderTypeQueryParam?: OrderTypesEnum
) => {
  const disabledItemsIdsPerFranchise = itemStatusRedux.disabledItemsIds;

  const disabledItemsIdsPerCurrentOrderType = getDisabledItemsPerOrderType(
    itemStatusRedux,
    orderTypeQueryParam
  );

  const allDisabledItemsIds = (disabledItemsIdsPerFranchise ?? []).concat(
    disabledItemsIdsPerCurrentOrderType ?? []
  );

  return allDisabledItemsIds;
};

const toMinutes = (time: string) => {
  const [hours, minutes] = time.split(":").map(Number);

  return hours * 60 + minutes;
};

/**
 * Converts a time string from "HH:mm:ss" (24-hour format) to "hh:mm A" (12-hour format).
 * @param time - The time string in "HH:mm:ss" format.
 * @returns The time string in "hh:mm A" format.
 */
export const formatTimeTo12Hour = (time: string): string => {
  const [hours, minutes] = time.split(":");
  const date = new Date();
  date.setHours(Number(hours), Number(minutes), 0);

  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
};

/**
 * Checks if the current time is within the specified availability range.
 * @param startTime - The start time in "HH:mm:ss" format.
 * @param endTime - The end time in "HH:mm:ss" format.
 * @param enabled - Whether the availability range is enabled.
 * @param timezone - the restaurant local timezone.
 * @param t - The i18n translation function.
 * @returns An object containing:
 *   - `isAvailable`: A boolean indicating if the item is currently available.
 *   - `availabilityMessage`: A localized string describing the availability in 12-hour format.
 *   - `fullMessage`: A localized string describing start time and end time availability in 12-hour format
 */
export const checkAvailability = (
  rawStartTime: string | null | undefined,
  rawEndTime: string | null | undefined,
  enabled: boolean,
  timezone: string,
  t?: (key: string, values?: Record<string, string>) => string
): {
  isAvailable: boolean;
  fullMessage?: string;
  availabilityMessage?: string;
} => {
  if (!enabled || (!rawStartTime && !rawEndTime)) {
    return { isAvailable: true };
  }
  // A window with only one end runs from midnight, or to the end of the day.
  const startTime = rawStartTime || "00:00";
  const endTime = rawEndTime || "23:59";

  const now = new Date();
  const currentTime = now.toLocaleTimeString("en-US", {
    timeZone: envConfig.enviroment === "local" ? "Africa/cairo" : timezone,
    hour12: false,
  });

  const formattedStartTime = formatTimeTo12Hour(startTime);
  const formattedEndTime = formatTimeTo12Hour(endTime);

  const fullMessage =
    t &&
    t("common:available-from-to", {
      startTime: formattedStartTime,
      endTime: formattedEndTime,
    });

  const availabilityMessage =
    t &&
    t("common:x-to-z", {
      startTime: formattedStartTime,
      endTime: formattedEndTime,
    });

  const currentMinutes = toMinutes(currentTime);
  const startMinutes = toMinutes(startTime);
  const endMinutes = toMinutes(endTime);

  let isAvailable: boolean;
  if (startMinutes < endMinutes) {
    isAvailable =
      currentMinutes >= startMinutes && currentMinutes <= endMinutes;
  } else {
    if (currentMinutes >= startMinutes || currentMinutes <= endMinutes) {
      isAvailable = true;
    } else {
      isAvailable = false;
    }
  }

  if (!isAvailable) {
    return { isAvailable, fullMessage, availabilityMessage };
  }

  return { isAvailable: true };
};

type DayWindow = { enabled?: boolean | null; days?: string[] | null };

const restrictsByDay = (window?: DayWindow | null): window is DayWindow =>
  !!window?.enabled && !!window.days?.length;

/**
 * Whether an item may be shown today, from its own window's days and its
 * categories'. Day limits have no design yet, so the menu leaves out what is
 * not available today instead of labelling it; hours keep their labels.
 *
 * Core's rule, so the menu never shows what checkout refuses: where any
 * category restricts by day, the categories' days replace the item's and all
 * must include today; otherwise the item's own days apply.
 */
export const isAvailableToday = (
  itemWindow: DayWindow | null | undefined,
  categoryWindows: Array<DayWindow | null | undefined>,
  timezone: string
): boolean => {
  const dayWindows = categoryWindows.filter(restrictsByDay);
  if (dayWindows.length === 0 && restrictsByDay(itemWindow)) {
    dayWindows.push(itemWindow);
  }
  if (dayWindows.length === 0) {
    return true;
  }

  const today = new Date()
    .toLocaleDateString("en-US", {
      timeZone: envConfig.enviroment === "local" ? "Africa/cairo" : timezone,
      weekday: "short",
    })
    .toUpperCase();

  return dayWindows.every((window) =>
    (window.days ?? []).some((day) => day?.toUpperCase() === today)
  );
};
